const queue = new Map<string, Promise<Blob>>();

interface DbData {
  id: number;
  index: number;
  date: number;
}

const CACHE_SIZE = 10;

function makePath(id: number, index: number) {
  return `/api/data/chartr/${id}/${index}`;
}

function add(data: DbData, store: IDBObjectStore) {
  return new Promise((resolve, reject) => {
    const query = store.add(data, [data.id, data.index]);

    query.onsuccess = () => resolve(true);
    query.onerror = () => reject(query.error);
  });
}

function open() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const ret = indexedDB.open('v1', 2);

    ret.onupgradeneeded = () => {
      const store = ret.result.createObjectStore('v1', { autoIncrement: true });
      store.createIndex('id, index', ['id', 'index'], { unique: true });
    };

    ret.onsuccess = () => resolve(ret.result);
    ret.onerror = () => reject(ret.error);
  });
}

function getStore(db: IDBDatabase) {
  return db.transaction('v1', 'readwrite').objectStore('v1');
}

async function addDb(id: number, index: number) {
  const db = await open();
  return add({ id, index, date: Date.now() }, getStore(db));
}

function getCount(store: IDBObjectStore) {
  const ret = store.count();

  return new Promise<number>((resolve, reject) => {
    ret.onsuccess = () => resolve(ret.result);
    ret.onerror = () => reject(ret.error);
  });
}

function getAll(store: IDBObjectStore) {
  const query = store.getAll();

  return new Promise<DbData[]>((resolve, reject) => {
    query.onsuccess = () => resolve(query.result);
    query.onerror = () => reject(query.error);
  });
}

function del(id: number, index: number, store: IDBObjectStore) {
  const exec = store.delete([id, index]);

  return new Promise<boolean>((resolve, reject) => {
    exec.onsuccess = () => resolve(true);
    exec.onerror = () => reject(exec.error);
  });
}

async function adjust() {
  const db = await open();

  const store = getStore(db);
  const count = await getCount(store);
  if (count <= CACHE_SIZE) {
    return;
  }

  const list = await getAll(store);
  list.sort((a, b) => a.date - b.date);
  const target = list.slice(0, count - CACHE_SIZE);

  const tasks = target.map((v) => caches.open('v1').then((c) => c.delete(makePath(v.id, v.index))));
  await Promise.all(tasks);

  const task2 = target.map((v) => del(v.id, v.index, getStore(db)));
  await task2;
}

async function update(id: number, index: number) {
  const db = await open();

  return new Promise<boolean>((resolve, reject) => {
    const store = getStore(db);
    const exec = store.put({ id, index, date: Date.now() }, [id, index]);

    exec.onsuccess = () => resolve(true);
    exec.onerror = () => reject(exec.error);
  });
}

function series<T>(p: (() => Promise<T>)[]) {
  if (p.length === 0) {
    return Promise.resolve();
  }

  const current = p[0]();
  return current.then(() => {
    series(p.slice(1));
  });
}

export async function getDataCache(id: number, index: number) {
  const path = makePath(id, index);

  const execute = queue.get(path);
  if (execute) {
    const val = await execute;
    queue.delete(path);
    return val;
  }

  const storage = await caches.open('v1');
  const res = await storage.match(path);
  if (res) {
    update(id, index);
    return res.blob();
  }

  const p = fetch(path).then((r) => r.blob());
  queue.set(path, p);

  const newBlob = await p;
  await addDb(id, index);
  await adjust();
  await storage.put(path, new Response(newBlob));

  return newBlob;
}

export function prefetch(id: number, length: number) {
  const tasks = Array.from({ length }).map((v, i) => () => getDataCache(id, i));
  return series(tasks);
}

export default {
  getDataCache,
};
